import { InfiniteData, QueryKey } from '@tanstack/react-query';
import { queryClient } from '@/lib/query/queryClient';
import { getHttpStatus } from '@/lib/sentry/http';
import { Primitive } from '@/lib/query/normalizeParams';
import {
    invalidateAllFetcherApiByPath,
    invalidateFetcherApiByPath,
    makeApiKey,
    updateApiCache,
} from '@/lib/query/fetcherCache';
import {
    ALPHAFEED_VISIBILITY,
    AlphaFeedPosition,
    AlphaFeedParticipantCounts,
    AlphaFeedSegmentId,
    AlphaFeedPositionAction,
    AlphaFeedPositionEventsParams,
    AlphaFeedPositionResponse,
    AlphaFeedPositionsParams,
    AlphaFeedPositionsResponse,
    AlphaFeedPulseParams,
    AlphaFeedRewardsResponse,
    AlphaFeedSsePositionEvent,
    AlphaFeedMetricKey,
    AlphaFeedTradingMetric,
} from '@/lib/types/alphafeedTypes';
import { HT_API_ROUTES } from '@/lib/http/hypertrackerHttpRequest';
import {
    ALPHAFEED_LIMITS,
    ALPHAFEED_PAIR_QUOTE,
    ALPHAFEED_TIMEFRAME_LADDER,
    AlphaFeedChartLayout,
    COIN_ICON_BASE_URL,
    COIN_ICON_LIGHT_BG,
    COIN_ICONS_WITH_LIGHT_BG,
    SEGMENT_STATS_WINDOW,
} from '@/pages/HypertrackerAlphafeed/alphafeedConstants';
import { getCoinName } from '@/lib/helpers/hypertrackerHelpers';
import { formatNumber } from '@/lib/helpers/helpers';
import { convertSecondsToDuration, formatTimeOfDeath } from '@/lib/helpers/durationHelpers';
import { HL_INTERVAL_MS, HlCandleBar, HlInterval } from '@/hooks/hypertracker/useHlCandles';

export type AlphafeedQueryParams = Record<string, Primitive>;

/** Logs AlphaFeed data flow in development only. */
export const alphafeedDebug = (label: string, payload?: unknown) => {
    if (!import.meta.env.DEV) return;
    console.log('[AlphaFeed]', label, payload ?? '');
};

/** True for 4xx responses, which are not worth retrying. */
export const isClientError = (error: unknown): boolean => {
    const status = getHttpStatus(error);
    return status !== undefined && status >= 400 && status < 500;
};

/** Clamps a limit to the endpoint range; non-finite values fall back to the default. */
export const clampLimit = (
    value: number | undefined,
    range: { min: number; max: number; default: number },
): number => {
    if (value === undefined || !Number.isFinite(value)) return range.default;
    return Math.min(range.max, Math.max(range.min, Math.round(value)));
};

const withoutUndefined = (params: Record<string, Primitive | undefined>): AlphafeedQueryParams => {
    const result: AlphafeedQueryParams = {};
    for (const [key, value] of Object.entries(params)) {
        if (value !== undefined) result[key] = value;
    }
    return result;
};

/** Query params exactly as sent to `GET alpha-feed/positions`; also used as the cache key. */
export const serializePositionsParams = (params: AlphaFeedPositionsParams): AlphafeedQueryParams =>
    withoutUndefined({
        status: params.status,
        coin: params.coin || undefined,
        limit: clampLimit(params.limit, ALPHAFEED_LIMITS.positions),
        cursor: params.cursor,
    });

/** Query params exactly as sent to `GET alpha-feed/pulse`; also used as the cache key. */
export const serializePulseParams = (params: AlphaFeedPulseParams): AlphafeedQueryParams =>
    withoutUndefined({
        limit: clampLimit(params.limit, ALPHAFEED_LIMITS.pulse),
        cursor: params.cursor,
    });

/** Query params exactly as sent to `GET alpha-feed/positions/:id/events`; also used as the cache key. */
export const serializePositionEventsParams = (
    params: AlphaFeedPositionEventsParams,
): AlphafeedQueryParams =>
    withoutUndefined({
        order: params.order,
        limit: clampLimit(params.limit, ALPHAFEED_LIMITS.events),
        cursor: params.cursor,
    });

/** A set that remembers at most `cap` IDs, evicting the oldest first. */
export const createBoundedIdSet = (cap: number) => {
    const ids = new Set<string>();
    return {
        /** Adds the ID and returns true if it was not seen before. */
        add(id: string): boolean {
            if (ids.has(id)) return false;
            ids.add(id);
            if (ids.size > cap) {
                const oldest = ids.values().next().value;
                if (oldest !== undefined) ids.delete(oldest);
            }
            return true;
        },
    };
};

/** Compares two non-negative bigint strings (event IDs) without precision loss. */
const compareBigintStrings = (a: string, b: string): number => {
    if (a.length !== b.length) return a.length - b.length;
    return a < b ? -1 : a > b ? 1 : 0;
};

const PATCHABLE_DECIMAL_FIELDS = [
    'size',
    'notionalUsd',
    'averageEntryPrice',
    'latestActivityAt',
    'exitSize',
    'exitNotionalUsd',
    'peakSize',
    'peakNotionalUsd',
    'grossRealisedPnlUsd',
    'feesUsd',
] as const;

/**
 * Applies an SSE event to a cached position. Returns null when a safe patch is impossible
 * (missing position, status change, or an older/duplicate event); REST refetch handles those.
 * `trader` and `qualification` are never touched.
 */
export const mergeSseSnapshot = (
    position: AlphaFeedPosition | undefined,
    event: AlphaFeedSsePositionEvent,
): AlphaFeedPosition | null => {
    if (!position || position.positionId !== event.position.positionId) return null;
    if (position.status !== event.position.status) return null;
    if (compareBigintStrings(event.eventId, position.latestEvent.eventId) <= 0) return null;

    const next = { ...position } as AlphaFeedPosition & Record<string, unknown>;
    for (const field of PATCHABLE_DECIMAL_FIELDS) {
        const value = event.position[field];
        if (typeof value === 'string') next[field] = value;
    }
    const leverage = event.position.leverage;
    if (typeof leverage === 'number' || leverage === null) next.leverage = leverage;

    return {
        ...next,
        latestEvent: {
            eventId: event.eventId,
            eventTime: event.eventTime,
            action: event.action,
            cause: event.cause,
            change: event.change,
        },
    } as AlphaFeedPosition;
};

/** True for `useQueryFetcher`-style keys of any AlphaFeed API path. */
export const isAlphafeedKey = (queryKey: QueryKey): boolean =>
    queryKey[0] === 'fetcher' &&
    queryKey[1] === 'api' &&
    typeof queryKey[2] === 'string' &&
    queryKey[2].startsWith(HT_API_ROUTES.ALPHA_FEED.PREFIX);

const isInfiniteKey = (queryKey: QueryKey) => queryKey[queryKey.length - 1] === 'infinite';

/** True when event ID `a` is newer than `b`. */
export const isEventIdNewer = (a: string, b: string): boolean => compareBigintStrings(a, b) > 0;

/** True for the cached feed list (`GET alpha-feed/positions`, infinite). */
export const isAlphafeedPositionsListKey = (queryKey: QueryKey): boolean =>
    isAlphafeedKey(queryKey) &&
    queryKey[2] === HT_API_ROUTES.ALPHA_FEED.POSITIONS &&
    isInfiniteKey(queryKey);

/** Newest `latestEvent.eventId` of a position across the cached feed list pages. */
export const getCachedFeedEventId = (positionId: string): string | undefined => {
    let eventId: string | undefined;
    queryClient
        .getQueriesData<InfiniteData<AlphaFeedPositionsResponse>>({
            predicate: ({ queryKey }) => isAlphafeedPositionsListKey(queryKey),
        })
        .forEach(([, data]) => {
            data?.pages.forEach((page) =>
                page.positions.forEach((position) => {
                    if (position.positionId !== positionId) return;
                    const id = position.latestEvent.eventId;
                    if (!eventId || isEventIdNewer(id, eventId)) eventId = id;
                }),
            );
        });
    return eventId;
};

/**
 * Patches the cached position detail from an SSE event. The feed list is not patched: it stays
 * frozen until the user applies the pending updates from the status bar (except follow/counter
 * totals, see `patchCachedParticipantCounts`).
 */
export const patchAlphafeedCaches = (event: AlphaFeedSsePositionEvent) => {
    const { positionId, qualification } = event.position;

    updateApiCache<AlphaFeedPositionResponse>(
        HT_API_ROUTES.ALPHA_FEED.POSITION(positionId, qualification.alphaSegmentId),
        undefined,
        (old) => {
            if (!old) return old;
            const merged = mergeSseSnapshot(old.position, event);
            return merged ? { ...old, position: merged } : old;
        },
    );
};

/**
 * Writes fresh follow/counter totals into the cached position detail and the feed list. Unlike
 * other fields, the totals bypass the frozen list; everything else waits for the pending updates.
 */
export const patchCachedParticipantCounts = (
    positionId: string,
    alphaSegmentId: AlphaFeedSegmentId,
    participantCounts: AlphaFeedParticipantCounts,
) => {
    updateApiCache<AlphaFeedPositionResponse>(
        HT_API_ROUTES.ALPHA_FEED.POSITION(positionId, alphaSegmentId),
        undefined,
        (old) => old && { ...old, position: { ...old.position, participantCounts } },
    );

    queryClient.setQueriesData<InfiniteData<AlphaFeedPositionsResponse>>(
        { predicate: ({ queryKey }) => isAlphafeedPositionsListKey(queryKey) },
        (old) => {
            const hasPosition = old?.pages.some((page) =>
                page.positions.some((position) => position.positionId === positionId),
            );
            // `undefined` skips the write: returning `old` would still mark the list as updated.
            if (!old || !hasPosition) return undefined;
            return {
                ...old,
                pages: old.pages.map((page) => ({
                    ...page,
                    positions: page.positions.map((position) =>
                        position.positionId === positionId
                            ? { ...position, participantCounts }
                            : position,
                    ),
                })),
            };
        },
    );
};

/** Position/segment projection touched by an SSE event. */
export interface AlphafeedProjectionRef {
    positionId: string;
    alphaSegmentId: string;
}

/** Refetches active queries affected by SSE events; the feed list is excluded (see above). */
export const invalidateAlphafeed = (projections: Iterable<AlphafeedProjectionRef>) => {
    invalidateFetcherApiByPath(HT_API_ROUTES.ALPHA_FEED.SUMMARY);
    for (const { positionId, alphaSegmentId } of projections) {
        invalidateAllFetcherApiByPath(
            HT_API_ROUTES.ALPHA_FEED.POSITION(positionId, alphaSegmentId),
        );
        invalidateAllFetcherApiByPath(HT_API_ROUTES.ALPHA_FEED.POSITION_EVENTS(positionId));
        invalidateAllFetcherApiByPath(HT_API_ROUTES.ALPHA_FEED.RECENT_OUTCOMES(positionId));
    }
};

/** HL query key prefixes of `useClearinghouseState` and `useFetchPnlAndActivity`. */
const WALLET_POSITIONS_QUERY_KEYS = new Set([
    'walletSubscribeClearinghouseState',
    'walletSubscribePnl',
]);

/**
 * Refetches the wallet's trading lifecycles. When the lifecycle closed (the user's position
 * closed or flipped side), the wallet positions and PnL are refetched too.
 */
export const invalidateAlphafeedTradingLifecycles = (address: string, isClosed: boolean) => {
    invalidateAllFetcherApiByPath(HT_API_ROUTES.ALPHA_FEED.TRADING_LIFECYCLES);
    if (!isClosed) return;
    const user = address.toLowerCase();
    queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
            WALLET_POSITIONS_QUERY_KEYS.has(String(queryKey[0])) &&
            typeof queryKey[1] === 'string' &&
            queryKey[1].toLowerCase() === user,
        refetchType: 'active',
    });
};

/** Refetches the feed list in place (loaded pages are re-requested in order). */
export const refreshAlphafeedPositions = () => {
    invalidateAllFetcherApiByPath(HT_API_ROUTES.ALPHA_FEED.POSITIONS);
};

/** Refetches the Pulse list (on `alphaFeedPulse` SSE frames). */
export const invalidateAlphafeedPulse = () => {
    invalidateAllFetcherApiByPath(HT_API_ROUTES.ALPHA_FEED.PULSE);
};

/** Refetches the volume rewards (on `alphaFeedRewardStatus` SSE frames). */
export const invalidateAlphafeedRewards = () => {
    invalidateAllFetcherApiByPath(HT_API_ROUTES.ALPHA_FEED.REWARDS);
};

/** Cached unlock state of one account; not loaded or no projection yet counts as locked. */
export const getCachedRewardUnlocked = (address: string) =>
    queryClient
        .getQueryData<AlphaFeedRewardsResponse>(makeApiKey(HT_API_ROUTES.ALPHA_FEED.REWARDS))
        ?.accounts?.find((item) => item.address.toLowerCase() === address.toLowerCase())
        ?.isUnlocked ?? false;

/** Refetches every active AlphaFeed query (used on SSE open). */
export const invalidateAllAlphafeed = () => {
    queryClient.invalidateQueries({
        predicate: ({ queryKey }) => isAlphafeedKey(queryKey),
        refetchType: 'active',
    });
};

/** Drops loaded pages and cursors of every AlphaFeed infinite query; active ones restart at page 1. */
export const resetAlphafeedPagination = () => {
    queryClient.resetQueries({
        predicate: ({ queryKey }) => isAlphafeedKey(queryKey) && isInfiniteKey(queryKey),
    });
};

const VISIBILITY_MODES = new Set<string>(Object.values(ALPHAFEED_VISIBILITY));

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value !== null;

/** Reads `visibility.mode` from a plain response or the first page of infinite data. */
export const extractVisibilityMode = (data: unknown): ALPHAFEED_VISIBILITY | undefined => {
    if (!isRecord(data)) return undefined;
    const source = Array.isArray(data.pages) ? data.pages[0] : data;
    if (!isRecord(source) || !isRecord(source.visibility)) return undefined;
    const { mode } = source.visibility;
    return typeof mode === 'string' && VISIBILITY_MODES.has(mode)
        ? (mode as ALPHAFEED_VISIBILITY)
        : undefined;
};

export interface AlphaFeedChartFrame {
    interval: HlInterval;
    intervalMs: number;
    /** First candle to fetch, ms (aligned to the interval). */
    startTime: number;
}

/**
 * Chart framing per the AlphaFeed chart spec: fixed `candles` count, at least `buffer` candles
 * before entry and after the current candle. Picks the smallest ladder interval whose span
 * (entry candle → current candle) fits `candles − 2 × buffer`. Closed positions stay live too,
 * so their chart keeps moving after the exit (FE-1376: they used to freeze `buffer` candles
 * after the exit, dropping the live price).
 * `minInterval` floors the ladder once the loaded bars proved a smaller interval doesn't fit.
 */
export const getAlphaFeedChartFrame = ({
    openedAtMs,
    nowMs,
    layout,
    minInterval = null,
}: {
    openedAtMs: number;
    nowMs: number;
    layout: AlphaFeedChartLayout;
    minInterval?: HlInterval | null;
}): AlphaFeedChartFrame => {
    const { candles, buffer } = layout;
    const maxSpan = candles - buffer * 2;
    const ladder = ALPHAFEED_TIMEFRAME_LADDER.slice(
        minInterval ? Math.max(0, ALPHAFEED_TIMEFRAME_LADDER.indexOf(minInterval)) : 0,
    );

    const interval =
        ladder.find((item) => {
            const tf = HL_INTERVAL_MS[item];
            return Math.floor(nowMs / tf) - Math.floor(openedAtMs / tf) + 1 <= maxSpan;
        }) ?? ALPHAFEED_TIMEFRAME_LADDER[ALPHAFEED_TIMEFRAME_LADDER.length - 1];
    const intervalMs = HL_INTERVAL_MS[interval];
    const endCandle = Math.floor(nowMs / intervalMs) * intervalMs;

    return {
        interval,
        intervalMs,
        startTime: endCandle - (candles - 1) * intervalMs,
    };
};

/** Next larger ladder interval; the largest one stays as is. */
export const getNextAlphaFeedInterval = (interval: HlInterval): HlInterval => {
    const index = ALPHAFEED_TIMEFRAME_LADDER.indexOf(interval);
    return ALPHAFEED_TIMEFRAME_LADDER[Math.min(index + 1, ALPHAFEED_TIMEFRAME_LADDER.length - 1)];
};

/**
 * Whether the shown bars (the last `candles`) include the entry candle with `buffer` bars before
 * it. Bars are counted, not times, so missing or unaligned HL candles are covered. A window that
 * isn't full shows every loaded bar, so a larger interval wouldn't reveal more.
 */
export const isAlphaFeedEntryInView = (
    bars: HlCandleBar[],
    openedAtMs: number,
    layout: AlphaFeedChartLayout,
): boolean => {
    if (bars.length < layout.candles) return true;
    const shown = bars.slice(-layout.candles);
    const firstAfterEntry = shown.findIndex((bar) => bar.time * 1000 > openedAtMs);
    const entryIndex = (firstAfterEntry === -1 ? shown.length : firstAfterEntry) - 1;
    return entryIndex >= layout.buffer;
};

/**
 * Price move from `fromPrice` to `toPrice` from the trader's side. Long gains when price rises,
 * short when it falls; `percent` is the absolute move.
 */
export const getPriceChange = (
    side: AlphaFeedPosition['side'],
    fromPrice: number,
    toPrice: number,
): { percent: number; isFavourable: boolean } | null => {
    if (!Number.isFinite(fromPrice) || !Number.isFinite(toPrice) || fromPrice <= 0 || toPrice <= 0)
        return null;
    const change = ((toPrice - fromPrice) / fromPrice) * 100;
    return {
        percent: Math.abs(change),
        isFavourable: side === 'long' ? change >= 0 : change <= 0,
    };
};

/** Next candle close (seconds) after `nowSec`, stepping from the last bar's open time. */
export const getNextCandleClose = (
    lastBarTimeSec: number,
    intervalSec: number,
    nowSec: number,
): number => {
    let next = lastBarTimeSec + intervalSec;
    if (next <= nowSec) {
        next += Math.ceil((nowSec - next) / intervalSec) * intervalSec;
        if (next <= nowSec) next += intervalSec;
    }
    return next;
};

const padTime = (value: number) => String(value).padStart(2, '0');

/** Candle countdown: `mm:ss`, or `hh:mm:ss` from one hour. */
export const formatCandleCountdown = (seconds: number): string => {
    const total = Math.max(Math.floor(seconds), 0);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const secs = total % 60;
    return hours > 0
        ? `${padTime(hours)}:${padTime(minutes)}:${padTime(secs)}`
        : `${padTime(minutes)}:${padTime(secs)}`;
};

/** Pair label shown on the chart, e.g. `BTCUSDC`; HIP-3 dex prefix is dropped. */
export const getPairLabel = (coin: string): string => `${getCoinName(coin)}${ALPHAFEED_PAIR_QUOTE}`;

/** Coin icon URL from the Hyperliquid CDN; HIP-3 dex prefix is kept lowercase, e.g. `xyz:BRENTOIL`. */
export const getCoinIconUrl = (coin: string): string => {
    const [dexOrName, name] = coin.split(':');
    const symbol = name ? `${dexOrName.toLowerCase()}:${name.toUpperCase()}` : coin.toUpperCase();
    return `${COIN_ICON_BASE_URL}/${symbol}.svg`;
};

/** Light backdrop for coins whose icon is a dark glyph without its own background. */
export const getCoinIconBgColor = (coin: string): string | undefined =>
    COIN_ICONS_WITH_LIGHT_BG.has(coin) ? COIN_ICON_LIGHT_BG : undefined;

/** Fallback order when the segment's window is missing: longest known window first. */
const METRIC_KEY_FALLBACK_ORDER: AlphaFeedMetricKey[] = ['30d', '7d', '72h'];

/**
 * Footer stats metric for the position's segment (7d for scalpers, 30d for day traders).
 * Falls back to the longest available window when the segment window is missing
 * (counter day traders only report 7d/72h). `window` is the metric key, e.g. `7d`.
 */
export const getAlphaFeedStatsMetric = (
    position: AlphaFeedPosition,
): { metric: AlphaFeedTradingMetric; window: string } | undefined => {
    const qualification = position.qualification;
    const metrics = qualification?.metrics;
    if (!metrics) return undefined;

    const preferred = SEGMENT_STATS_WINDOW[qualification.alphaSegmentId];
    const window = [preferred, ...METRIC_KEY_FALLBACK_ORDER].find((key) => key && metrics[key]);
    if (!window) return undefined;

    return { metric: metrics[window], window };
};

/** Decimal string → finite number; null when missing or not numeric. */
export const parseDecimal = (value: string | null | undefined): number | null => {
    if (value === undefined || value === null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

/**
 * Position size as a percentage of the trader's typical (median) size in the segment stats window:
 * `abs(notionalUsd) / medianSizeUsd × 100`.
 * Returns `null` when the median is missing/non-positive or the action has no size row.
 */
export const getPercentageOfTypicalSize = (position: AlphaFeedPosition): number | null => {
    const { action } = position.latestEvent;
    if (action !== 'open' && action !== 'increase' && action !== 'reduce') return null;

    const median = parseDecimal(getAlphaFeedStatsMetric(position)?.metric.medianSizeUsd);
    if (median === null || median <= 0) return null;

    const notional = parseDecimal(position.notionalUsd);
    if (notional === null) return null;

    const percentage = (Math.abs(notional) / median) * 100;
    return Number.isFinite(percentage) ? percentage : null;
};

const MIN_DISPLAY_SIZE_RATIO = 0.1;
const HOLD_TIME_UNITS = ['days', 'hours', 'minutes', 'seconds'];

/** Size vs typical as a label, e.g. `1.9x`; `—` when unknown. */
export const getSizeRatioLabel = (position: AlphaFeedPosition): string => {
    const percentageOfTypicalSize = getPercentageOfTypicalSize(position);
    if (percentageOfTypicalSize === null) return '—';
    const sizeRatio = percentageOfTypicalSize / 100;
    return sizeRatio < MIN_DISPLAY_SIZE_RATIO
        ? `<${MIN_DISPLAY_SIZE_RATIO}x`
        : `${formatNumber(sizeRatio, 1)}x`;
};

/** Closed age, hold time and entry → exit move; `null` for open positions. */
export const getClosedPositionInfo = (
    position: AlphaFeedPosition,
    locale: string,
    justNowLabel: string,
) =>
    position.status === 'closed'
        ? {
              closedAge: formatTimeOfDeath(position.closedAt, locale, justNowLabel),
              holdTime: convertSecondsToDuration(
                  (new Date(position.closedAt).getTime() - new Date(position.openedAt).getTime()) /
                      1000,
                  locale,
                  { units: HOLD_TIME_UNITS, maxUnitsCount: 2 },
              ),
              journey: getPriceChange(
                  position.side,
                  Number(position.averageEntryPrice),
                  Number(position.averageExitPrice),
              ),
          }
        : null;

/** HyperLiquid coin name: HIP-3 assets are prefixed with their dex (e.g. `xyz:XYZ100`). */
export const getHlCoin = (position: Pick<AlphaFeedPosition, 'coin' | 'dex'>): string => {
    if (!position.dex || position.coin.includes(':')) return position.coin;
    return `${position.dex}:${position.coin}`;
};

const PRICE_AXIS_SIGNIFICANT_DIGITS = 4;
const PRICE_AXIS_MAX_DECIMALS = 6;

/** Price-axis decimals: 4 significant digits (54.58, 65000, 0.001234), capped at 6 decimals. */
export const getPriceDecimals = (price: number): number => {
    if (!Number.isFinite(price) || price <= 0) return 2;
    const magnitude = Math.floor(Math.log10(price));
    const decimals = PRICE_AXIS_SIGNIFICANT_DIGITS - 1 - magnitude;
    return Math.min(Math.max(decimals, 0), PRICE_AXIS_MAX_DECIMALS);
};

export type AlphaFeedFillIcon = `${AlphaFeedPosition['side']}_${AlphaFeedPositionAction}`;
export type AlphaFeedFillPlacement = 'above' | 'below';

const FILL_ACTIONS = new Set<string>(['open', 'increase', 'reduce', 'close']);

/**
 * Fill marker icon and placement: buy-side fills (long open/increase, short reduce/close)
 * sit under the candle, sell-side fills above it. `null` for an unknown action.
 */
export const getFillMarkerKind = (
    side: AlphaFeedPosition['side'],
    action: string,
): { icon: AlphaFeedFillIcon; placement: AlphaFeedFillPlacement } | null => {
    if (!FILL_ACTIONS.has(action)) return null;
    const isAdding = action === 'open' || action === 'increase';
    const isBuy = side === 'long' ? isAdding : !isAdding;
    return {
        icon: `${side}_${action as AlphaFeedPositionAction}`,
        placement: isBuy ? 'below' : 'above',
    };
};
